import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import UserNotRegisteredError from "@/components/UserNotRegisteredError";

import SiteLayout from "@/components/SiteLayout";
import Home from "@/pages/Home";

const HighestEfficiencyPanels = lazy(() => import("@/pages/HighestEfficiencyPanels"));
const BestHighEfficiencyPanels = lazy(() => import("@/pages/BestHighEfficiencyPanels"));
const BestSolarPanels = lazy(() => import("@/pages/BestSolarPanels"));
const SolarPanelForEcoFlow = lazy(() => import("@/pages/SolarPanelForEcoFlow"));
const SolarPanelsForVanLife = lazy(() => import("@/pages/SolarPanelsForVanLife"));
const SolarPanelCalculator = lazy(() => import("@/pages/SolarPanelCalculator"));
const SolarCellVsPanel = lazy(() => import("@/pages/SolarCellVsPanel"));
const MonoVsPoly = lazy(() => import("@/pages/MonoVsPoly"));
const BifacialPanels = lazy(() => import("@/pages/BifacialPanels"));
const FlexiblePanels = lazy(() => import("@/pages/FlexiblePanels"));
const BestRVPanels = lazy(() => import("@/pages/BestRVPanels"));
const BestPortablePanels = lazy(() => import("@/pages/BestPortablePanels"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const AffiliateDisclosurePage = lazy(() => import("@/pages/AffiliateDisclosurePage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const BestSolarGeneratorPanels = lazy(() => import("@/pages/BestSolarGeneratorPanels"));
const SolarGuides = lazy(() => import("@/pages/SolarGuides"));
const SolarGuidePage = lazy(() => import("@/pages/SolarGuidePage"));
const ReviewModeration = lazy(() => import("@/pages/ReviewModeration"));
const PageNotFound = lazy(() => import("./lib/PageNotFound"));

function RouteFallback() {
  return (
    <div className="min-h-[35vh] flex items-center justify-center" role="status" aria-live="polite">
      <span className="sr-only">Loading page</span>
      <div className="w-7 h-7 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" aria-hidden="true" />
    </div>
  );
}

const AuthenticatedApp = () => {
  const { authError, navigateToLogin } = useAuth();

  // Public content should render immediately. Auth/public-settings checks continue
  // in the background instead of blocking FCP/LCP on a public marketing site.
  if (authError) {
    if (authError.type === "user_not_registered") {
      return <UserNotRegisteredError />;
    }

    if (authError.type === "auth_required") {
      navigateToLogin();
      return null;
    }
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/highest-efficiency-solar-panels" element={<HighestEfficiencyPanels />} />
          <Route path="/best-high-efficiency-solar-panels" element={<BestHighEfficiencyPanels />} />
          <Route path="/best-solar-panels" element={<BestSolarPanels />} />
          <Route path="/solar-panel-for-ecoflow" element={<SolarPanelForEcoFlow />} />
          <Route path="/solar-panels-for-van-life" element={<SolarPanelsForVanLife />} />
          <Route path="/solar-panel-calculator" element={<SolarPanelCalculator />} />
          <Route path="/solar-cell-vs-solar-panel" element={<SolarCellVsPanel />} />
          <Route path="/monocrystalline-vs-polycrystalline-solar-panels" element={<MonoVsPoly />} />
          <Route path="/bifacial-solar-panels" element={<BifacialPanels />} />
          <Route path="/flexible-solar-panels" element={<FlexiblePanels />} />
          <Route path="/best-solar-panels-for-rv" element={<BestRVPanels />} />
          <Route path="/best-portable-solar-panels" element={<BestPortablePanels />} />
          <Route path="/best-solar-panels-for-solar-generators" element={<BestSolarGeneratorPanels />} />
          <Route path="/solar-guides" element={<SolarGuides />} />
          <Route path="/solar-guides/:slug" element={<SolarGuidePage />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/review/:slug" element={<ReviewPage />} />
          <Route path="/admin/product-reviews" element={<ReviewModeration />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
